import { LogoutOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Typography,
} from "antd";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import { formatDateTime } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  AccountSuspension,
  PrivacyPolicyConfirmation,
  PrivacyPolicyState,
  UserPersonalData,
} from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { LanguageSwitcher } from "../layout/LanguageSwitcher";
import { PrivacyPolicyText } from "./privacyPolicyText";

/**
 * The privacy policy gate. While the acceptance is pending, every other
 * endpoint answers 403 (ApplicationController#require_privacy_policy_acceptance);
 * only logout, this screen, the personal data and the account deactivation stay
 * reachable — see PRIVACY_POLICY_EXEMPT_ACTIONS.
 *
 * The screen therefore renders outside AppLayout: a navigation whose entries
 * all lead to a refusal would only mislead, which is why the old application
 * showed this page without its sidebar as well.
 *
 * Three things the user must be able to do here, all of them carried over from
 * the old accept_privacy_policy view: read which data is processed, inspect
 * their own stored data, and — if they do not consent — deactivate the account
 * instead of accepting.
 */
export function PrivacyPolicyPage() {
  const { t } = useTranslation();
  const { logout, clearPrivacyPolicyRequirement } = useAuth();
  const navigate = useNavigate();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [checked, setChecked] = useState(false);
  const [personalDataOpen, setPersonalDataOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendForm] = Form.useForm<{ email: string }>();

  const showError = useCallback(
    (error: unknown) => {
      // The API phrases the reason itself and in the user's language; only a
      // transport-level failure has no notification to show.
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const state = useQuery({
    queryKey: ["main", "privacy-policy"],
    queryFn: () => apiFetch<PrivacyPolicyState>("/main/accept_privacy_policy"),
  });

  const confirmPolicy = useMutation({
    mutationFn: () =>
      apiFetch<PrivacyPolicyConfirmation>("/main/confirm_privacy_policy", {
        method: "POST",
        body: { confirm_privacy_policy_check: checked },
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      if (data.policy_confirmation_needed) return;

      clearPrivacyPolicyRequirement();
      // Everything the shell asked for while the gate was closed came back as a
      // 403; those cached refusals have to go before the app is shown.
      queryClient.clear();
      navigate("/", { replace: true });
    },
    onError: showError,
  });

  const personalData = useQuery({
    queryKey: ["main", "personal-data"],
    queryFn: () => apiFetch<UserPersonalData>("/main/show_user_personal_data"),
    enabled: personalDataOpen,
  });

  const suspend = useMutation({
    mutationFn: (email: string) =>
      apiFetch<AccountSuspension>("/main/suspend_current_account", {
        method: "DELETE",
        body: { email },
      }),
    onSuccess: async (data) => {
      notify(data.notifications);
      if (!data.account_suspended) return;

      // The server has already revoked the JWT, so the local session must go
      // with it — otherwise the UI stays up with a token that is dead.
      setSuspendOpen(false);
      await logout();
    },
    onError: showError,
  });

  const askForDeactivation = () => {
    Modal.confirm({
      title: t("dialog.confirmation_title"),
      content: t("user.privacy_policy_suspend_account_question"),
      okText: t("dialog.answer_yes"),
      okButtonProps: { danger: true },
      cancelText: t("dialog.answer_no"),
      onOk: () => {
        suspendForm.resetFields();
        setSuspendOpen(true);
      },
    });
  };

  const confirmationNeeded = state.data?.policy_confirmation_needed ?? true;
  const acceptedOn = state.data?.user_privacy_policy_acceptance_date;

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: 24,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <Card
        style={{ width: "100%", maxWidth: 820 }}
        title={t("user.privacy_policy")}
        extra={
          <Space>
            <LanguageSwitcher />
            <Button icon={<LogoutOutlined />} onClick={() => void logout()}>
              {t("auth.logout")}
            </Button>
          </Space>
        }
      >
        {state.isPending ? (
          <Spin />
        ) : (
          <>
            <PrivacyPolicyText onShowPersonalData={() => setPersonalDataOpen(true)} />

            <Divider />

            {confirmationNeeded ? (
              <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
                <Checkbox checked={checked} onChange={(event) => setChecked(event.target.checked)}>
                  <Typography.Text type="danger">
                    {t("user.privacy_policy_confirmation")} *
                  </Typography.Text>
                </Checkbox>

                <Space wrap>
                  <Button
                    type="primary"
                    disabled={!checked}
                    loading={confirmPolicy.isPending}
                    onClick={() => confirmPolicy.mutate()}
                  >
                    {t("user.privacy_policy_confirm")}
                  </Button>
                  <Button danger onClick={askForDeactivation}>
                    {t("user.privacy_policy_suspend_account")}
                  </Button>
                </Space>
              </Space>
            ) : (
              <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
                <Typography.Text>
                  {t("user.privacy_policy_confirmed_on", {
                    confirmation_date: formatDateTime(acceptedOn),
                  })}
                </Typography.Text>
                <Button type="primary" onClick={() => navigate("/", { replace: true })}>
                  {t("auth.access_main_page")}
                </Button>
              </Space>
            )}
          </>
        )}
      </Card>

      <Modal
        open={personalDataOpen}
        title={t("user.privacy_policy_personal_data")}
        onCancel={() => setPersonalDataOpen(false)}
        footer={null}
        width={600}
      >
        {personalData.isPending ? (
          <Spin />
        ) : personalData.error ? (
          <Typography.Text type="danger">
            {personalData.error instanceof Error
              ? personalData.error.message
              : String(personalData.error)}
          </Typography.Text>
        ) : (
          // Same fields and the same labels as the old dhtmlXWindows form, so a
          // user recognises what they saw before.
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t("activerecord.attributes.user.first_name")}>
              {personalData.data?.first_name}
            </Descriptions.Item>
            <Descriptions.Item label={t("activerecord.attributes.user.last_name")}>
              {personalData.data?.last_name}
            </Descriptions.Item>
            <Descriptions.Item label={t("user.headers.address")}>
              {personalData.data?.address}
            </Descriptions.Item>
            <Descriptions.Item label={t("activerecord.attributes.user.email")}>
              {personalData.data?.email}
            </Descriptions.Item>
            <Descriptions.Item label={t("activerecord.attributes.user.private_email")}>
              {personalData.data?.private_email}
            </Descriptions.Item>
            <Descriptions.Item label={t("activerecord.attributes.user.phone")}>
              {personalData.data?.phone}
            </Descriptions.Item>
            <Descriptions.Item label={t("activerecord.attributes.user.mobile")}>
              {personalData.data?.mobile}
            </Descriptions.Item>
            <Descriptions.Item label={t("congregation.congregation")}>
              {personalData.data?.congregation}
            </Descriptions.Item>
            <Descriptions.Item label={t("activerecord.models.circumscription")}>
              {personalData.data?.circumscription}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        open={suspendOpen}
        title={t("user.privacy_policy_suspend_account_confirm_title")}
        onCancel={() => setSuspendOpen(false)}
        okText={t("user.privacy_policy_suspend_account_confirm_button")}
        okButtonProps={{ danger: true, loading: suspend.isPending }}
        onOk={() => suspendForm.submit()}
        destroyOnHidden
      >
        <Form
          form={suspendForm}
          layout="vertical"
          onFinish={({ email }) => suspend.mutate(email)}
        >
          <Form.Item
            name="email"
            label={t("user.privacy_policy_suspend_account_confirm_input")}
            // Only checked for presence here. The comparison belongs to the
            // server, which holds the current address: the cached login data
            // can be one confirmed e-mail change behind, and a client-side
            // check would then reject the correct answer.
            rules={[{ required: true }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
